import classnames from 'classnames'
import React from 'react'

import { Dialog, DialogBody, DialogHeader, DialogStateReturn } from '../../../../components/dialog'
import { Heading } from '../../../../components/typography'
import { useRefetchData } from '../hooks'
import { ETFHoldingDataType, ETFHoldingsDialogNavigation, ETFHoldingsDialogType } from '../types'
import { getNormalizedStandardData } from '../utils'
import { Navigation } from './Navigation'
import { StandardMap } from './StandardMap'
import { TableChart } from './Table'

interface IEtfHoldingsDialog {
  etfTicker: string
  type: ETFHoldingsDialogType
  dialogState: DialogStateReturn
  handleDialogToggle: (value?: ETFHoldingsDialogType) => void
}

const VIEW_SETTINGS: ETFHoldingsDialogNavigation = {
  navType: 'dialog',
  viewTypes: [
    { icon: 'chartTable', chartType: ETFHoldingsDialogType.Table },
    { icon: 'chartTreemap', chartType: ETFHoldingsDialogType.Map },
  ],
}

export function ETFHoldingsDialog({ type, etfTicker, dialogState, handleDialogToggle }: IEtfHoldingsDialog) {
  const isMounted = dialogState.useState('mounted')
  const fetchedData = useRefetchData(etfTicker, ETFHoldingDataType.Full)
  const data = React.useMemo(() => getNormalizedStandardData(fetchedData.data), [fetchedData.data])
  if (!data || !isMounted) return null

  return (
    <Dialog
      state={dialogState}
      aria-label="ETF Holdings"
      className={classnames('h-3/4 w-3/4', { 'max-w-3xl': type === ETFHoldingsDialogType.Table })}
      data-testid="etf-holdings-dialog"
    >
      <DialogHeader className="relative" asHeading={false}>
        <div className="flex w-full items-center justify-between pr-4">
          <Heading level={4} className="my-0">
            {`${etfTicker.toUpperCase()}: ${data.rowData.length} Holdings`}
          </Heading>
          <Navigation
            navType={VIEW_SETTINGS.navType}
            viewTypes={VIEW_SETTINGS.viewTypes}
            onNavigationChange={(value) => handleDialogToggle(value)}
            active={type}
          />
        </div>
      </DialogHeader>
      <DialogBody className="flex shrink grow flex-col" data-testid="etf-holdings-dialog-body">
        {type === ETFHoldingsDialogType.Table ? (
          <TableChart type={`dialog-${type}`} data={data} />
        ) : (
          <StandardMap type={`dialog-${type}`} data={data} />
        )}
      </DialogBody>
    </Dialog>
  )
}
